import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle } from "@my-saas/ui";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router";

import { useAlertFeed } from "@/client/hooks/use-alert-feed";
import { useTenantInfo, useTenantOrefAlertLog } from "@/client/services/tenant";

/**
 * Tenant live alert feed dashboard. Combines:
 *  - real-time socket.io push from AlertsGateway (alerts as they arrive)
 *  - polled REST OrefAlertLog for the recent history backstop (last 50 from DB)
 *  - tenant info card with status badges
 */
export const TenantDashboardPage = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { data: info } = useTenantInfo(tenantSlug);
  const { data: log } = useTenantOrefAlertLog(tenantSlug);
  const feed = useAlertFeed({ enabled: !!tenantSlug });

  return (
    <>
      <Helmet>
        <title>{info?.name ? `${info.name} · dashboard` : "Tenant dashboard"}</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">{info?.name ?? tenantSlug}</h1>
            <p className="text-sm text-muted-foreground">
              {info ? (
                <>
                  Status: <Badge variant={info.status === "ACTIVE" ? "primary" : "secondary"}>{info.status}</Badge>
                  {info.runtimeLoaded ? (
                    <span className="ml-2 text-emerald-600">runtime loaded</span>
                  ) : (
                    <span className="ml-2 text-amber-600">runtime not loaded</span>
                  )}
                </>
              ) : (
                "Loading…"
              )}
            </p>
          </div>
          <div className="text-xs text-muted-foreground">
            WS:{" "}
            <span className={feed.connected ? "text-emerald-600 font-mono" : "text-destructive font-mono"}>
              {feed.connected ? "connected" : "disconnected"}
            </span>
            {feed.error && <span className="ml-2 text-destructive">{feed.error}</span>}
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Live alert feed</CardTitle>
            <CardDescription>
              Real-time push from the alert engine. The first {feed.alerts.length === 0 ? "feed update" : "alerts"} appear here as they're processed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {feed.alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Waiting for live alerts… (live ones land here instantly)</p>
            ) : (
              <ul className="space-y-1">
                {feed.alerts.map((a) => (
                  <li
                    key={`${a.id}-${a.receivedAt}`}
                    className="flex items-center gap-3 rounded border bg-muted/40 px-3 py-2 text-sm"
                  >
                    <Badge variant="secondary" className="font-mono text-xs">{a.source}</Badge>
                    {a.matched ? (
                      <Badge variant="primary" className="text-xs">matched</Badge>
                    ) : (
                      <Badge variant="secondary" className="text-xs">no-match</Badge>
                    )}
                    {a.blocked && <Badge variant="error" className="text-xs">{a.reason ?? "blocked"}</Badge>}
                    <span className="flex-1 truncate font-medium" dir="rtl">{a.title}</span>
                    <span className="text-xs text-muted-foreground" dir="rtl">
                      {a.locations.slice(0, 3).join(", ") || "—"}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(a.receivedAt).toLocaleTimeString()}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent OREF alert log (last 50)</CardTitle>
            <CardDescription>Persisted history from Postgres. Auto-refreshes every 10s.</CardDescription>
          </CardHeader>
          <CardContent>
            {!log || log.length === 0 ? (
              <p className="text-sm text-muted-foreground">No alerts logged for this tenant yet.</p>
            ) : (
              <ul className="space-y-1">
                {log.map((a) => (
                  <li key={a.id} className="grid grid-cols-[auto,1fr,auto] items-center gap-3 rounded px-3 py-2 text-sm">
                    <div className="flex gap-1">
                      <Badge variant="secondary" className="text-xs">{a.source}</Badge>
                      {a.isMatch && <Badge className="text-xs">matched</Badge>}
                      {a.dedupBlocked && <Badge variant="error" className="text-xs">dedup</Badge>}
                    </div>
                    <div>
                      <div className="font-medium" dir="rtl">{a.title}</div>
                      <div className="text-xs text-muted-foreground" dir="rtl">
                        {a.locations.slice(0, 6).join(", ")}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      <div>{new Date(a.receivedAt).toLocaleString()}</div>
                      <div className="font-mono">#{a.alertId}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};
